import { useState, type ReactNode } from 'react';
import { Pagination } from './pagination';
import { TableSkeleton } from './skeleton';
import { Symbol } from './symbol';
import { matchesQuery } from './table-search';

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /**
   * Hide this column on the stacked mobile card view. Use it for columns that
   * are only meaningful next to their neighbours (internal IDs, timestamps a
   * card already implies) — the card has no header row to explain them.
   */
  hideOnMobile?: boolean;
  /**
   * Promote this column to the card's title on mobile. Exactly one column per
   * table should set it; without it the first column is used.
   */
  primary?: boolean;
}

/** A dropdown filter over one column. Rows match when `row[key]` equals the
 *  chosen option value (or when nothing is chosen). */
export interface FilterDef {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string;
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => ReactNode;
  perPage?: number;
  /** Show a shimmer skeleton instead of rows while the data loads. */
  loading?: boolean;
  /** When provided, the Filter button opens a panel of these dropdowns. */
  filters?: FilterDef[];
  /** Base file name for the CSV export (without extension). */
  exportName?: string;
  /**
   * Switches the table to server-side paging: `data` is treated as one already-
   * fetched page and rendered whole, while the footer pager reports the server's
   * totals. Without this the table would slice the page a second time and show
   * its own pager alongside the caller's.
   *
   * Note that search/filter then only span the current page — use it for lists
   * whose search is served by the API.
   */
  /**
   * What the search box matches for a row. Defaults to every field of the row
   * object (nested objects included); pass the rendered values instead when
   * columns show derived text (a formatted date, a status label).
   */
  searchFields?: (row: T) => unknown[];
  /**
   * Controlled search for server-searched lists: the box shows `searchValue`,
   * reports edits through `onSearchChange`, and rows are NOT filtered again
   * client-side (the server already matched on fields the row may not carry).
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  serverPagination?: {
    page: number;
    totalItems: number;
    onPageChange: (page: number) => void;
  };
}

type Density = 'compact' | 'default' | 'comfortable';

// Escape a value for a CSV cell (quote when it contains a comma/quote/newline).
function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Generic admin data table — design system v2 §09 `.table-wrap`: a radius-16
 * panel with a toolbar (search pill, filter chip, actions), a sunk header row,
 * 14px cells and a footer with the count and pager.
 * Client-side search, optional column filters, density toggle, CSV export and
 * pagination over an in-memory dataset.
 *
 * **Responsive behaviour.** Below `md` the table becomes a stack of cards, one
 * per row, each cell a label/value pair. A `min-w-[600px]` table inside a
 * horizontal scroller — the previous approach — technically fits on a phone
 * but is unusable: you cannot read a row without losing the column you are
 * comparing against, and the horizontal scroll fights the page's vertical one.
 * From `md` up it is the full table, unchanged.
 *
 * The toolbar reflows too: search takes the full width on its own row, and the
 * density toggle (a pointer-precision affordance with no meaning on a stacked
 * card view) is hidden below `lg`.
 */
export function DataTable<T>({
  columns,
  data,
  rowKey,
  searchPlaceholder = 'Search…',
  onRowClick,
  actions,
  perPage = 15,
  loading = false,
  filters,
  exportName = 'export',
  serverPagination,
  searchFields,
  searchValue,
  onSearchChange,
}: DataTableProps<T>) {
  const [localSearch, setLocalSearch] = useState('');
  const serverSearch = onSearchChange !== undefined;
  const search = serverSearch ? (searchValue ?? '') : localSearch;
  const setSearch = serverSearch ? onSearchChange : setLocalSearch;
  const [localPage, setLocalPage] = useState(1);
  const [density, setDensity] = useState<Density>('default');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);

  // A single page identity for the rest of the component: the caller's when the
  // table is server-paged, local state otherwise. Everything downstream (search
  // reset, the footer pager) is written against these and needs no branching.
  const page = serverPagination ? serverPagination.page : localPage;
  const setPage = serverPagination ? serverPagination.onPageChange : setLocalPage;

  if (loading) {
    return <TableSkeleton rows={perPage} cols={columns.length + (actions ? 1 : 0)} />;
  }

  const hasFilters = Boolean(filters && filters.length > 0);
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  const filtered = data.filter((row) => {
    const rec = row as Record<string, unknown>;
    const searchOk =
      serverSearch || matchesQuery(searchFields ? searchFields(row) : Object.values(rec), search);
    const filterOk = Object.entries(activeFilters).every(
      ([key, val]) => !val || String(rec[key] ?? '') === val,
    );
    return searchOk && filterOk;
  });
  // Server-paged tables receive exactly one page of rows, so they render as-is
  // and defer page state to the caller.
  const serverPaged = serverPagination;
  const totalPages = serverPaged
    ? Math.max(1, Math.ceil(serverPaged.totalItems / perPage))
    : Math.ceil(filtered.length / perPage);
  const paginated = serverPaged ? filtered : filtered.slice((page - 1) * perPage, page * perPage);

  // Cell padding per density; the default (14px) comes from `.data-table td`.
  const rowPadding =
    density === 'compact' ? 'py-2' : density === 'comfortable' ? 'py-[18px]' : '';

  // The column that titles each card in the mobile view.
  const primaryColumn = columns.find((c) => c.primary) ?? columns[0];
  const secondaryColumns = columns.filter(
    (c) => c !== primaryColumn && !c.hideOnMobile,
  );

  const cellValue = (row: T, col: Column<T>): ReactNode =>
    col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '—');

  // Export the currently-visible rows (search + filters applied) as CSV.
  const exportCsv = () => {
    const table = [
      columns.map((c) => c.label),
      ...filtered.map((row) => columns.map((c) => (row as Record<string, unknown>)[c.key])),
    ];
    const csv = table.map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportName}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const empty = (
    <div className="flex flex-col items-center gap-1.5">
      <span className="mb-1 grid h-12 w-12 place-items-center rounded-full bg-admin-soft text-admin-ink">
        <Symbol name={search || activeFilterCount > 0 ? 'search_off' : 'inbox'} size={24} />
      </span>
      <p className="text-[14.5px] font-semibold text-ink-900">
        {search.trim() ? `No matches for “${search.trim()}”` : activeFilterCount > 0 ? 'No rows match these filters' : 'Nothing here yet'}
      </p>
      {(search || activeFilterCount > 0) && (
        <>
          <p className="text-[13.5px] text-ink-500">Every word must appear in one of the columns.</p>
          <button
            type="button"
            className="btn btn-secondary btn-sm mt-2"
            onClick={() => {
              setSearch('');
              setActiveFilters({});
            }}
          >
            {search ? 'Clear search' : 'Clear filters'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="panel overflow-hidden">
      {/* Toolbar — stacks below sm so the search field keeps a usable width. */}
      <div className="flex flex-col gap-3 border-b border-surface-line px-4 py-3.5 sm:flex-row sm:items-center">
        <label className="relative flex-1 sm:max-w-xs">
          <span className="sr-only">Search</span>
          <Symbol
            name="search"
            size={18}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500"
          />
          <input
            className="search-pill"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <div className="flex items-center gap-2 sm:ml-auto">
          {/* Density is a pointer-precision control with nothing to act on in
              the stacked card view, so it only appears at desktop widths. */}
          <div className="seg hidden lg:inline-flex" role="group" aria-label="Row density">
            {(['compact', 'default', 'comfortable'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDensity(d)}
                aria-pressed={density === d}
                className="!px-2.5 !py-1 !text-[12.5px]"
              >
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              aria-expanded={showFilters}
              aria-pressed={showFilters || activeFilterCount > 0}
              className="fchip"
            >
              <Symbol name="tune" size={16} />
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          )}
          <button type="button" onClick={exportCsv} className="btn btn-secondary btn-sm">
            <Symbol name="download" size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filter panel — one column per filter on phones, wrapping row above. */}
      {hasFilters && showFilters && (
        <div className="grid grid-cols-1 gap-4 border-b border-surface-line bg-surface-sunk px-4 py-4 sm:flex sm:flex-wrap sm:items-end">
          {filters!.map((f) => (
            <label key={f.key} className="flex flex-col gap-1.5 sm:min-w-[180px]">
              <span className="field-label">{f.label}</span>
              <select
                value={activeFilters[f.key] ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  setActiveFilters((p) => ({ ...p, [f.key]: value }));
                  setPage(1);
                }}
                className="input input-sm"
              >
                <option value="">All</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          ))}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setActiveFilters({});
                setPage(1);
              }}
              className="btn btn-ghost btn-sm h-9"
            >
              <Symbol name="close" size={16} /> Clear filters
            </button>
          )}
        </div>
      )}

      {/* Stacked card list — below md. */}
      <div className="divide-y divide-surface-line md:hidden">
        {paginated.map((row) => {
          const Wrapper = onRowClick ? 'button' : 'div';
          return (
            <Wrapper
              key={rowKey(row)}
              {...(onRowClick
                ? { type: 'button' as const, onClick: () => onRowClick(row) }
                : {})}
              className={`flex w-full flex-col gap-2 p-4 text-left transition-colors ${
                onRowClick ? 'hover:bg-surface-sunk active:bg-admin-soft' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 text-[14.5px] font-semibold text-ink-900">
                  {cellValue(row, primaryColumn)}
                </div>
                {actions && (
                  // Nested interactive content inside a button wrapper would be
                  // invalid HTML and would swallow the row click, so the actions
                  // cell stops propagation and sits in its own span.
                  <span
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="presentation"
                    className="shrink-0"
                  >
                    {actions(row)}
                  </span>
                )}
              </div>
              <dl className="grid grid-cols-[minmax(0,7rem)_1fr] gap-x-3 gap-y-1.5">
                {secondaryColumns.map((col) => (
                  <div key={col.key} className="contents">
                    <dt className="text-[12.5px] font-semibold text-ink-500">{col.label}</dt>
                    <dd className="min-w-0 text-[14px] text-ink-900">{cellValue(row, col)}</dd>
                  </div>
                ))}
              </dl>
            </Wrapper>
          );
        })}
        {paginated.length === 0 && <div className="px-4 py-12 text-center">{empty}</div>}
      </div>

      {/* Full table — md and up. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={col.align === 'right' ? '!text-right' : ''}
                >
                  {col.label}
                </th>
              ))}
              {actions && (
                <th scope="col" className="!text-right">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginated.map((row) => (
              <tr
                key={rowKey(row)}
                className={`transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${rowPadding} ${col.align === 'right' ? 'text-right' : ''}`}
                  >
                    {cellValue(row, col)}
                  </td>
                ))}
                {actions && (
                  <td
                    className={`text-right ${rowPadding}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr className="hover:!bg-transparent">
                <td colSpan={columns.length + (actions ? 1 : 0)} className="!py-12 text-center">
                  {empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — shared component, used across every admin list page. */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalItems={serverPaged ? serverPaged.totalItems : filtered.length}
        perPage={perPage}
        onPageChange={setPage}
      />
    </div>
  );
}
